import { useMatch, useSearchParams } from 'react-router-dom';
import { FallbackProps } from 'react-error-boundary';
import { isAxiosError } from 'axios';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@antimatterhq/ui/components/accordion';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Text } from '@antimatterhq/ui/components/typography';
import { Skeleton } from '@antimatterhq/ui/components/skeleton';
import { GlobalError } from '@antimatterhq/ui/components/globalError';
import {
  RuleMetadataReview,
  MitreInformationReview,
  RuleSourceReview,
  ObservablesReview,
  NotableOutputReview,
} from './_partials';
import { TwoColumnLayout } from '@/components/container';
import { TocWrapper } from '@/components/tableOfContents/ToCWrapper';
import { TableOfContentsProvider } from '@/contexts/TableOfContentContext';
import { TableOfContents } from '@/components/tableOfContents/TableOfContents';
import { BackLink } from '@/components/link/back/BackLink';
import { SuspenseErrorBoundary } from '@/components/errorBoundary/SuspenseErrorBoundary';
import { useGetRule } from '@/hooks/core/useGetRule';

const sections = [
  {
    id: 'rule-metadata',
    title: 'Rule',
  },
  {
    id: 'mitre-information',
    title: 'Mitre ATT&CK information',
  },
  {
    id: 'rule-source',
    title: 'Source',
  },
  {
    id: 'observables',
    title: 'Observables',
  },
  {
    id: 'notable-output',
    title: 'Notable output',
  },
] as const;

function DetectionRuleReviewSuspense() {
  const match = useMatch('/configure/detection-rules/review/:name');
  const { name = '' } = match?.params || {};
  const [searchParams] = useSearchParams();
  const version = searchParams.get('version');

  const { data: rule } = useGetRule({
    name,
    version: version ? Number(version) : undefined,
  });

  return (
    <main data-testid="detection-rules-review-page">
      <TableOfContentsProvider>
        <TwoColumnLayout.Root>
          <TwoColumnLayout.LeftColumn>
            <div>
              <BackLink fallbackRoute="/configure/detection-rules" />
            </div>
            <div className="w-full">
              <Accordion
                type="multiple"
                className="w-full"
                defaultValue={sections.map(({ id }) => id)}
              >
                {sections.map(({ id, title }) => (
                  <TocWrapper key={id} displayName={title} name={id}>
                    <AccordionItem value={id} key={id} className="border-b-0">
                      <AccordionTrigger>
                        <Text size="lg" className="font-bold">
                          {title}
                        </Text>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="relative">
                          {id === 'rule-metadata' && (
                            <RuleMetadataReview rule={rule} />
                          )}
                          {id === 'mitre-information' && (
                            <MitreInformationReview rule={rule} />
                          )}
                          {id === 'rule-source' && (
                            <RuleSourceReview rule={rule} />
                          )}
                          {id === 'observables' && (
                            <ObservablesReview rule={rule} />
                          )}
                          {id === 'notable-output' && (
                            <NotableOutputReview rule={rule} />
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </TocWrapper>
                ))}
              </Accordion>
            </div>
          </TwoColumnLayout.LeftColumn>
          <TwoColumnLayout.RightColumn>
            <TableOfContents />
          </TwoColumnLayout.RightColumn>
        </TwoColumnLayout.Root>
      </TableOfContentsProvider>
    </main>
  );
}

const ReviewDetectionRulesErrorBoundaryFallback: React.FC<FallbackProps> = ({
  resetErrorBoundary,
  error,
}) => {
  const match = useMatch('/configure/detection-rules/review/:name');
  const { name = '' } = match?.params || {};
  const isNotFoundError = isAxiosError(error) && error.response?.status === 404;

  if (isNotFoundError)
    return (
      <GlobalError
        title="Detection rule not found"
        description={`Detection rule "${name}" could not be found`}
        onRetry={async () => resetErrorBoundary()}
        learnMoreLink="/configure/detection-rules"
        learnMoreText="Go to detection rules"
        retryText="Retry"
        className="h-[75vh]"
      />
    );

  const errorMessage = isAxiosError(error)
    ? (error.response?.statusText ?? error.message)
    : error.message;

  return (
    <GlobalError
      title="Error loading detection rule"
      description={errorMessage}
      onRetry={async () => resetErrorBoundary()}
      retryText="Retry"
      className="h-[75vh]"
    />
  );
};

function ReviewDetectionRulesSuspenseFallback() {
  return (
    <>
      <Spacer size="lg" />

      <TwoColumnLayout.Root>
        <TwoColumnLayout.LeftColumn>
          <div className="flex flex-col gap-4">
            <Skeleton className="h-8 w-56" />

            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        </TwoColumnLayout.LeftColumn>
        <TwoColumnLayout.RightColumn>
          <nav className="sticky top-[40px] flex flex-col gap-4">
            <Text size="lg" className="mb-4 font-bold">
              Table of contents
            </Text>
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </nav>
        </TwoColumnLayout.RightColumn>
      </TwoColumnLayout.Root>
    </>
  );
}

const DetectionRuleReview = () => {
  return (
    <SuspenseErrorBoundary
      suspenseFallback={<ReviewDetectionRulesSuspenseFallback />}
      errorBoundaryFallback={ReviewDetectionRulesErrorBoundaryFallback}
    >
      <DetectionRuleReviewSuspense />
    </SuspenseErrorBoundary>
  );
};

export default DetectionRuleReview;
